/**
 * Focus Trap Hook
 * Traps focus within a modal or dialog element
 *
 * @package
 */

import { useEffect, useRef } from '@wordpress/element';

/**
 * Custom hook to trap focus within an element
 *
 * @param {boolean} isActive     Whether the focus trap is active.
 * @param {Object}  containerRef Ref to the container element.
 */
export const useFocusTrap = ( isActive, containerRef ) => {
	const previousActiveElement = useRef( null );

	useEffect( () => {
		if ( ! isActive || ! containerRef.current ) {
			return;
		}

		const container = containerRef.current;
		const ownerDocument = container.ownerDocument || document;

		// Store the previously focused element.
		previousActiveElement.current = ownerDocument.activeElement;

		// Get all focusable elements within the container.
		const getFocusableElements = () => {
			const focusableSelectors = [
				'button:not([disabled])',
				'[href]',
				'input:not([disabled])',
				'select:not([disabled])',
				'textarea:not([disabled])',
				'[tabindex]:not([tabindex="-1"])',
			].join( ', ' );

			return Array.from( container.querySelectorAll( focusableSelectors ) ).filter(
				el => {
					// Filter out hidden elements.
					const style = window.getComputedStyle( el );
					return style.display !== 'none' && style.visibility !== 'hidden';
				}
			);
		};

		// Focus the first focusable element.
		const initialFocusableElements = getFocusableElements();
		if ( initialFocusableElements.length > 0 ) {
			initialFocusableElements[ 0 ].focus();
		}

		// Handle Tab key to cycle through focusable elements.
		const handleKeyDown = e => {
			if ( e.key !== 'Tab' ) {
				return;
			}

			const currentFocusableElements = getFocusableElements();
			if ( currentFocusableElements.length === 0 ) {
				return;
			}

			const firstElement = currentFocusableElements[ 0 ];
			const lastElement = currentFocusableElements[ currentFocusableElements.length - 1 ];
			const activeElement = ownerDocument.activeElement;

			if ( e.shiftKey ) {
				// Shift + Tab: move backwards.
				if ( activeElement === firstElement ) {
					e.preventDefault();
					lastElement.focus();
				}
			} else if ( activeElement === lastElement ) {
				// Tab: move forwards.
				e.preventDefault();
				firstElement.focus();
			}
		};

		container.addEventListener( 'keydown', handleKeyDown );

		// Cleanup: restore focus to previous element.
		return () => {
			container.removeEventListener( 'keydown', handleKeyDown );

			// Restore focus to the element that triggered the modal.
			if ( previousActiveElement.current && previousActiveElement.current.focus ) {
				previousActiveElement.current.focus();
			}
		};
	}, [ isActive, containerRef ] );
};
